// @flow
// ----------------------------------------------------------------------
// <copyright file="Izenda.SourceTreeItem.js" company="Izenda">
//  Copyright (c) 2015 Izenda, Inc.
//  ALL RIGHTS RESERVED
//
//  The entire contents of this file is protected by U.S. and
//  International Copyright Laws. Unauthorized reproduction,
//  reverse-engineering, and distribution of all or any portion of
//  the code contained in this file is strictly prohibited and may
//  result in severe civil and criminal penalties and will be
//  prosecuted to the maximum extent possible under the law.
//
//  RESTRICTIONS
//
//  THIS SOURCE CODE AND ALL RESULTING INTERMEDIATE FILES
//  ARE CONFIDENTIAL AND PROPRIETARY TRADE
//  SECRETS OF IZENDA INC. THE REGISTERED DEVELOPER IS
//  LICENSED TO DISTRIBUTE THE PRODUCT AND ALL ACCOMPANYING .NET
//  CONTROLS AS PART OF AN EXECUTABLE PROGRAM ONLY.
//
//  THE SOURCE CODE CONTAINED WITHIN THIS FILE AND ALL RELATED
//  FILES OR ANY PORTION OF ITS CONTENTS SHALL AT NO TIME BE
//  COPIED, TRANSFERRED, SOLD, DISTRIBUTED, OR OTHERWISE MADE
//  AVAILABLE TO OTHER INDIVIDUALS WITHOUT EXPRESS WRITTEN CONSENT
//  AND PERMISSION FROM IZENDA INC.
//
//  CONSULT THE END USER LICENSE AGREEMENT(EULA FOR INFORMATION ON
//  ADDITIONAL RESTRICTIONS.
// </copyright>
// ----------------------------------------------------------------------

/**
 * SourceTreeItem component
 */

import React from 'react';
import PropTypes from 'prop-types';
import connect from 'share/enhancers/Izenda.Connect';
import classnames from 'classnames';
import constants from 'logic/data/Izenda.ImportDataModelConstants';
import TextHighLight from 'share/components/controls/Izenda.TextHighLight';
import Input from 'share/components/controls/Izenda.Input';
import { isFunction, get, isEmpty, cond, matches, constant, filter } from 'lodash/fp';

const { NODE_TYPE } = constants;
const NUMBER_ZERO = 0;
const NUMBER_CHECK_INDEX = -1;

const SourceTreeItem = (props: any) => {
  /**
   * handle onCheck event
   * @param  {Object} event
   */
  const onCheck = (event: any) => {
    
  };

  const isEmptyGroup = (node: any) => {
    return node.isTypeGroup() && childCount === NUMBER_ZERO;
  };

  /**
   * show number of children
   * @param  {QuerySourceNode} node
   * @param  {Number} childCount Child Nodes count
   */
  const showNumChildren = (node: any, count: Number) => {
    if (node.isTypeGroup() && node.childNodes) {
      return <span> ({count}) </span>;
    }
    return null;
  };

  const getNodeStyle = (node: any) => {
    const hasChild = !isEmpty(node.childNodes);
    const isDataObject =
      [NODE_TYPE.STORE_PROCEDURE, NODE_TYPE.FUNCTION, NODE_TYPE.TABLE, NODE_TYPE.VIEW].indexOf(
        get('name', node.parentNode)
      ) > NUMBER_CHECK_INDEX;

    if (node.isTypeGroup()) {
      //  node.group != null > table, proc, view
      return hasChild ? getNodeStyleForQuerySouce(node) : null;
    } else if (isDataObject) {
      // data source: child of QuerySourceType
      return getNodeStyleForDataObject(node);
    } else if (hasChild) {
      // connection node, data object node
      // do nothing
      return checkConsistentGroupObject(node);
    } else {
      // no child > field
      return getNodeStyleForField(node);
    }
  };

  const getNodeStyleForQuerySouce = (node: any) => {
    let icon;
    if (hasChildNodesChanges(node, constants.DETECTED_CHANGE_LEVEL.ADDED).length > NUMBER_ZERO) {
      icon = getPhysicalChangesIcon(constants.DETECTED_CHANGE_LEVEL.ADDED);
    } else if (hasChildNodesChanges(node, constants.DETECTED_CHANGE_LEVEL.NONE).length > NUMBER_ZERO) {
      icon = getPhysicalChangesIcon(constants.DETECTED_CHANGE_LEVEL.NONE);
    }

    return {
      icon: icon
    };
  };

  const getNodeStyleForDataObject = (node: any) => {
    const parentType = get('name', node.parentNode);
    const hasChild = !isEmpty(node.childNodes);
    const className = classnames({
      'u-colorBlue': checkLinkToCopiedReport(node)
    });

    return {
      icon:
        hasChild || [NODE_TYPE.STORE_PROCEDURE, NODE_TYPE.FUNCTION].indexOf(parentType) > NUMBER_CHECK_INDEX
          ? getPhysicalChangesIcon(node.change)
          : null,
      className: className
    };
  };

  const checkConsistentGroupObject = (node: any) => {
    return {
      icon: !isEmpty(node.childNodes) && getPhysicalChangesIcon(node.change)
    };
  };

  /**
   * filter physical changes for TypeGroup level
   * @param  {QuerySourceNode} node
   */
  const hasChildNodesChanges = (node: any, changeLevel: Number) => {
    return filter(o => {
      return o.change >= changeLevel;
    }, node.childNodes);
  };

  /**
   * filter physical changes for TypeGroup level
   * @param  {Number} change
   */
  const getPhysicalChangesIcon = (change: Number) => {
    return change > constants.DETECTED_CHANGE_LEVEL.NONE ? (
      <span className='icon-attention warning-color' title={constants.INCONSISTENT_DATA_MODEL} />
    ) : change === constants.DETECTED_CHANGE_LEVEL.NONE ? (
      <span className='icon-ok-circled success-color' title={constants.CONSISTENT_DATA_MODEL} />
    ) : null;
  };

  /**
   * render changes icon for field level
   * @param  {QuerySourceNode} node
   */
  const getNodeStyleForField = (node: any) => {
    const isAdded = checkChangedStatus(node.change, constants.DETECTED_CHANGE_LEVEL.ADDED);
    const isEdited = checkChangedStatus(node.change, constants.DETECTED_CHANGE_LEVEL.MODIFIED);

    const className = classnames({
      u_textLineThrough: isAdded,
      'icon-move-data warning-color': isEdited,
      'u-colorBlue': checkLinkToCopiedReport(node)
    });

    const title = cond([
      [matches({ isNew: true }), constant(constants.CONSISTENT_DATA_MODEL)],
      [matches({ isAdded: true }), constant(constants.INCONSISTENT_DATA_MODEL)],
      [matches({ isEdited: true }), constant(constants.CHANGED_DATA_TYPE)]
    ])({ isAdded: isAdded, isEdited: isEdited });

    if (isAdded) {
      return {
        className: className
      };
    } else {
      return {
        className: classnames({
          'u-colorBlue': checkLinkToCopiedReport(node)
        }),
        icon: <span className={className} title={title} />
      };
    }
  };

  /**
   * Check Changed Status
   * @param  {Number} change
   * @param  {Number} changeLevel
   */
  const checkChangedStatus = (change: Number, changeLevel: Number): Boolean => {
    return change === changeLevel;
  };

  const checkLinkToCopiedReport = (node: any) => {
    return (
      !node.checked &&
      (get('parentNode.initData.belongToCopiedReport', node) ||
        get('parentNode.belongToCopiedReport', node) ||
        get('initData.belongToCopiedReport', node) ||
        get('belongToCopiedReport', node))
    );
  };

  const { node, toggleCollapse } = props;
  const filterItem = props.filter;

  const containerClasses = classnames('checkbox', {
    'disabled-elm': isEmptyGroup(node)
  });
  const showCheckboxClass = classnames({
    hidden: !props.showCheckbox
  });

  const childCount = node.childCount(function(child) {
    if (isFunction(props.showSelected)) {
      return props.showSelected(child);
    }

    return props.showSelected ? child.dataSource.checked : true;
  });

  const toggle = () => {
    toggleCollapse();
  };

  // Get style for node: icon, className & style
  const nodeStyle = props.showIcon ? getNodeStyle(node) : {};

  return (
    <div className={containerClasses} key={node.id}>
      <Input
        type='checkbox'
        checked={node.dataSource.checked}
        onChange={onCheck}
        className={showCheckboxClass}
      />
      <label
        className='u_paddingLeft u_textTruncate u_alignBottom'
        key={node.id}
        disabled={isEmptyGroup(node) ? 'disabled' : null}
      >
        <TextHighLight
          highlight={filterItem}
          text={node.name}
          className={get('className', nodeStyle)}
          style={get('style', nodeStyle)}
          tooltip={node.name}
        />
        {showNumChildren(node, childCount)}
        {get('icon', nodeStyle)}
      </label>

      {!isEmptyGroup(node) && <i className='icon-toggle-triangle' onClick={toggle} />}
    </div>
  );
};

SourceTreeItem.propTypes = {
  node: PropTypes.object.isRequired,
  filter: PropTypes.string,
  showCheckbox: PropTypes.bool,
  showIcon: PropTypes.bool,
  showSelected: PropTypes.oneOfType([PropTypes.func, PropTypes.bool])
};

export default connect()(SourceTreeItem);



// WEBPACK FOOTER //
// ./ui/modules/Settings/DataSetup/Import/DataModel/AdvancedImport/SourceTree/Izenda.SourceTreeItem.js